import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Slider,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  SkipPrevious as PrevIcon,
  SkipNext as NextIcon,
  CenterFocusStrong as CenterFocusIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Help as HelpIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface TileViewer360Props {
  tileName: string;
  tileImages: string[];
  onClose?: () => void;
}

const TileViewer360: React.FC<TileViewer360Props> = ({
  tileName,
  tileImages,
  onClose
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, imageIndex: 0 });
  const [autoRotate, setAutoRotate] = useState(false);
  const [dragSensitivity] = useState(2); // Lower = more sensitive
  const [showInstructions, setShowInstructions] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  // Check screen size for desktop/mobile detection
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 600);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const animationRef = useRef<number | null>(null);

  // Auto-rotation effect - only when enabled
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRotate && !isDragging) {
      interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % tileImages.length);
      }, 800); // Slower auto-rotation (800ms vs 150ms)
    }
    return () => clearInterval(interval);
  }, [autoRotate, isDragging, tileImages.length]);

  // Handle smooth image transitions
  const changeImage = useCallback((newIndex: number, smooth = true) => {
    if (newIndex < 0) newIndex = tileImages.length - 1;
    if (newIndex >= tileImages.length) newIndex = 0;
    
    if (smooth && animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    setCurrentImageIndex(newIndex);
  }, [tileImages.length]);

  // Mouse events for drag rotation
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setAutoRotate(false); // Stop auto-rotation when user interacts
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      imageIndex: currentImageIndex,
    });
  }, [currentImageIndex]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    // Calculate rotation based on horizontal movement
    const rotationAmount = Math.floor(Math.abs(deltaX) / (100 / dragSensitivity));
    const direction = deltaX > 0 ? 1 : -1;
    
    if (rotationAmount > 0) {
      const newIndex = dragStart.imageIndex + (direction * rotationAmount);
      let finalIndex = ((newIndex % tileImages.length) + tileImages.length) % tileImages.length;
      changeImage(finalIndex, false);
    }

    // Handle zoom with vertical movement (optional)
    const zoomDelta = -deltaY / 1000;
    if (Math.abs(deltaY) > 10) {
      setZoom(prev => Math.max(0.5, Math.min(3, prev + zoomDelta)));
    }

    // Update position for panning when zoomed
    if (zoom > 1) {
      setPosition({
        x: Math.max(-200, Math.min(200, deltaX / 3)),
        y: Math.max(-200, Math.min(200, deltaY / 3)),
      });
    }
  }, [isDragging, dragStart, dragSensitivity, zoom, changeImage, tileImages.length]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch events for mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setAutoRotate(false);
    setDragStart({
      x: touch.clientX,
      y: touch.clientY,
      imageIndex: currentImageIndex,
    });
  }, [currentImageIndex]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStart.x;
    
    // More sensitive touch rotation
    const rotationAmount = Math.floor(Math.abs(deltaX) / (50 / dragSensitivity));
    const direction = deltaX > 0 ? 1 : -1;
    
    if (rotationAmount > 0) {
      const newIndex = dragStart.imageIndex + (direction * rotationAmount);
      let finalIndex = ((newIndex % tileImages.length) + tileImages.length) % tileImages.length;
      changeImage(finalIndex, false);
    }
  }, [isDragging, dragStart, dragSensitivity, changeImage, tileImages.length]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Scroll wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? 0.95 : 1.05;
    setZoom(prev => Math.max(0.5, Math.min(3, prev * zoomFactor)));
  }, []);

  // Manual controls
  const handlePrevImage = useCallback(() => {
    setAutoRotate(false);
    changeImage(currentImageIndex - 1, true);
  }, [currentImageIndex, changeImage]);

  const handleNextImage = useCallback(() => {
    setAutoRotate(false);
    changeImage(currentImageIndex + 1, true);
  }, [currentImageIndex, changeImage]);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(3, prev * 1.2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(0.5, prev / 1.2));
  }, []);

  const resetView = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setCurrentImageIndex(0);
    setAutoRotate(false);
  }, []);

  const toggleAutoRotate = useCallback(() => {
    setAutoRotate(!autoRotate);
  }, [autoRotate]);

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#000',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Main Tile Image */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          ref={imageRef}
          src={tileImages[currentImageIndex]}
          alt={`${tileName} - View ${currentImageIndex + 1}`}
          sx={{
            maxWidth: '90%',
            maxHeight: '90%',
            objectFit: 'contain',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(255,255,255,0.1)',
            userSelect: 'none',
            pointerEvents: 'none',
            transition: 'opacity 0.2s ease-in-out',
          }}
        />
      </Box>

      {/* Navigation Controls - Mobile Optimized */}
      <Paper
        sx={{
          position: 'absolute',
          bottom: { xs: 10, sm: 20 },
          left: '50%',
          transform: 'translateX(-50%)',
          p: { xs: 1, sm: 2 },
          backgroundColor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: { xs: '16px', sm: '20px' },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          justifyContent: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          maxWidth: { xs: '90%', sm: 'none' },
        }}
      >
        {/* Image Navigation */}
        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center' }}>
          <Tooltip title="Previous Image">
            <IconButton onClick={handlePrevImage} size="small">
              <PrevIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />
            </IconButton>
          </Tooltip>
          
          <Chip 
            label={`${currentImageIndex + 1}/${tileImages.length}`} 
            size="small" 
            variant="outlined"
            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
          />
          
          <Tooltip title="Next Image">
            <IconButton onClick={handleNextImage} size="small">
              <NextIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Zoom Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          <Tooltip title="Zoom Out">
            <IconButton onClick={handleZoomOut} size="small">
              <ZoomOutIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />
            </IconButton>
          </Tooltip>
          <Slider
            value={zoom}
            min={0.5}
            max={3}
            step={0.1}
            onChange={(_, value) => setZoom(value as number)}
            sx={{ width: { xs: 60, sm: 100 } }}
            size="small"
          />
          <Tooltip title="Zoom In">
            <IconButton onClick={handleZoomIn} size="small">
              <ZoomInIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Auto-Rotation Toggle */}
        <Tooltip title={autoRotate ? "Stop Auto Rotation" : "Start Auto Rotation"}>
          <IconButton 
            onClick={toggleAutoRotate} 
            size="small"
            sx={{ 
              color: autoRotate ? '#1a237e' : '#666',
              backgroundColor: autoRotate ? 'rgba(26, 35, 126, 0.1)' : 'transparent',
            }}
          >
            {autoRotate ? <PauseIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} /> : <PlayIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />}
          </IconButton>
        </Tooltip>

        {/* Reset Button */}
        <Tooltip title="Reset View">
          <IconButton onClick={resetView} size="small">
            <CenterFocusIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Info Panel - Mobile Optimized */}
      <Paper
        sx={{
          position: 'absolute',
          top: { xs: 10, sm: 20 },
          left: { xs: 10, sm: 20 },
          p: { xs: 1, sm: 2 },
          backgroundColor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px',
          maxWidth: { xs: 200, sm: 300 },
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#1a237e', 
            fontWeight: 'bold', 
            mb: 1,
            fontSize: { xs: '0.875rem', sm: '1.25rem' }
          }}
        >
          {tileName} - 360° View
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 0.5, sm: 1 }, 
          flexWrap: 'wrap', 
          mb: 1 
        }}>
          <Chip 
            label={`View ${currentImageIndex + 1}/${tileImages.length}`} 
            size="small"
            sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
          />
          <Chip 
            label={`Zoom: ${(zoom * 100).toFixed(0)}%`} 
            size="small"
            sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
          />
        </Box>
        <Box sx={{ 
          display: { xs: 'none', sm: 'flex' }, 
          gap: 1, 
          flexWrap: 'wrap' 
        }}>
          <Chip 
            label={autoRotate ? "Auto Rotating" : "Manual Control"} 
            size="small" 
            color={autoRotate ? "primary" : "default"} 
          />
          {isDragging && <Chip label="Dragging" size="small" color="secondary" />}
        </Box>
      </Paper>

      {/* Help Button for Mobile */}
      <IconButton
        onClick={() => setShowInstructions(!showInstructions)}
        sx={{
          position: 'absolute',
          top: { xs: 10, sm: 20 },
          right: { xs: 10, sm: 20 },
          backgroundColor: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          display: { xs: 'flex', sm: 'none' },
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,1)',
          }
        }}
        size="small"
      >
        <HelpIcon sx={{ fontSize: '1.2rem', color: '#1a237e' }} />
      </IconButton>

      {/* Instructions Panel - Desktop Always Visible, Mobile Toggle */}
      {(showInstructions || isDesktop) && (
        <Paper
          sx={{
            position: 'absolute',
            top: { xs: 50, sm: 20 },
            right: { xs: 10, sm: 60 },
            p: { xs: 1.5, sm: 2 },
            backgroundColor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            maxWidth: { xs: '280px', sm: '250px' },
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            zIndex: 10,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#1a237e', 
                fontSize: { xs: '0.75rem', sm: '0.875rem' } 
              }}
            >
              User Controls:
            </Typography>
            <IconButton 
              onClick={() => setShowInstructions(false)}
              size="small"
              sx={{ display: { xs: 'flex', sm: 'none' }, ml: 1 }}
            >
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
            🖱️ Drag left/right to rotate
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
            🔍 Scroll wheel to zoom
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
            ⏭️ Use ◀️ ▶️ for manual navigation
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
            ⏯️ Toggle auto-rotation
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#666', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
            📱 Touch-friendly on mobile
          </Typography>
        </Paper>
      )}

      {/* Image Preloader */}
      <Box sx={{ display: 'none' }}>
        {tileImages.map((src, index) => (
          <img 
            key={index} 
            src={src} 
            alt="" 
            onLoad={() => {}}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TileViewer360;